// GEOMETRIA EDITORULUI DE SOLUȚII — partea fără desen a lui `EditorStructura`.
//
// Editorul desenează geometria PRIMARĂ a unei soluții de structură: elementele
// structurale verticale (ESV — stâlpi, lamele, pereți de beton) și grinzile
// dintre ele, pe fiecare nivel. Schema e cea a serverului
// (`structure/geometrie/tipuri.py`): coordonatele în milimetri, secțiunile în
// centimetri, înălțimea grinzii în metri, nivelurile ca indexuri de etaj de jos
// în sus. Aici stă tot ce se poate socoti fără ecran — cadrul de desen,
// amprentele, operațiile, lipirea și cotele — ca să se poată testa singur.
//
// CADRUL DE DESEN. Coordonatele planșelor sunt Stereo 70 (sute de kilometri în
// milimetri): prea mari pentru un SVG, care desenează în simplă precizie. Deci
// editorul lucrează în METRI, față de colțul din stânga-sus al arhitecturii, cu
// Y-ul întors — pe ecran nordul planșei e sus, ca pe orice plan.
//
// NODUL NU E CENTRUL. Un ESV are un nod — capătul grinzilor — și o secțiune
// care poate aluneca de-a lungul axei lui (`ofs_mm`): la capătul unui zid
// betonul intră în zid, nodul rămâne pe grilă. Mutarea mută nodul, iar
// redimensionarea crește secțiunea în jurul centrului ei.

import { taietor } from "../viewer/taietor";
import { t } from "../i18n";

export type Pt = [number, number];

export type Brat = { L_cm: number; b_cm: number; ofs_mm: number; la_mm: number };
export type SectiuneEsv = {
  b_cm: number;
  L_cm: number;
  ofs_mm?: number;
  brate?: Brat[];
  /** ce a purtat dim-ul Pasului 4 în afara schemei (o amprentă în L/T) — se
   *  păstrează cât nu se schimbă forma */
  dim_extra?: Record<string, unknown>;
};
export type Esv = {
  id: string;
  axa: "X" | "Y";
  nod: Pt;
  ofs_mm?: number;
  sectiune_per_nivel: Record<string, SectiuneEsv>;
  baza: "fundatie" | { grinda: string; nivel: number };
  transfer?: unknown[];
  pereche?: string | null;
  /** celălalt capăt al aceleiași linii de zid, oglindit față de axa lungă a
   *  barei (`geometrie/bloc_inalt`); `pereche` e oglinda față de axa dintre
   *  frontoane */
  geaman?: string | null;
  /** orbita de simetrie a elementului (`geometrie/orbite.py`) */
  orbita?: string | null;
  tip?: string | null;
};
export type SectiuneGrinda = { b_cm: number; h_m: number; dim_extra?: Record<string, unknown> };
export type Grinda = {
  id: string;
  nod_i: string;
  nod_j: string;
  sectiune_per_nivel: Record<string, SectiuneGrinda>;
  familie?: string | null;
  cadru?: boolean;
  ofs_i?: Pt | null;
  ofs_j?: Pt | null;
};
/** Un perete de ZIDĂRIE CONFINATĂ al nivelului-proeminență
 *  (`structure/zidarie_confinata.pereti_structurali`): pe axa lui, în mm,
 *  în cadrul clădirii — același cu al nodurilor —, cu stâlpișorii lui
 *  (`esv` = elementul de beton care urcă de jos și e el stâlpișorul;
 *  fără `esv`, stâlpișorul e al zidului și nu stă în `esv[]`). */
export type ZidConfinat = {
  id: string;
  nivel: number;
  axa: "X" | "Y";
  cota_mm: number;
  de_la_mm: number;
  pana_la_mm: number;
  grosime_mm: number;
  stalpisori?: { la_mm: number; tip?: string; esv?: string | null }[];
  latit_la_grinzi?: { de_la_mm: number; grinzi: string[] } | null;
};
export type Geometrie = {
  esv: Esv[];
  grinzi: Grinda[];
  grinzi_transfer?: string[];
  /** zidăria confinată a nivelului-proeminență: se desenează, nu se editează */
  zidarie_confinata?: ZidConfinat[];
  fundatie?: unknown;
  /** ce a schimbat omul la placa de la capul fiecărui nivel: inele adăugate
   *  și scoase (`editorPlaci`, `geometrie/tipuri.placi`) */
  placi?: Record<string, { adaugate: Pt[][]; scoase: Pt[][] }>;
};

export type PereteContext = {
  id: string | null;
  colturi: Pt[];
  grosime_m?: number | null;
  material?: string;
  rol?: string;
  /** false = Pasul 4 nu pune ESV în el la nivelul lui; atunci `motiv` spune de ce */
  zid?: boolean;
  motiv?: string;
};
export type NivelContext = {
  index: number;
  nivel: string;
  /** planșeul din document și cota lui: după ele se așază planul arhitecturii
   *  peste geometrie (`calcArhitectura`) */
  storey?: string | null;
  level?: number | null;
  inaltime_m: number | null;
  pereti: PereteContext[];
  goluri: { id: string | null; colturi: Pt[]; tip?: string | null }[];
  /** axele desenate de arhitect (grila modulară), cu numele din bulină:
   *  pe ele se centrează elementele; pe arhitectura fără axe desenate sunt
   *  cele DERIVATE de structură din ziduri (`derivata`) */
  axe?: { nume: string | null; a: Pt; b: Pt; derivata?: boolean }[];
  amprenta: Pt[] | null;
};
export type ContextEditor = { niveluri: NivelContext[]; theta_grade: number };

/** Latura minimă de execuție a unui ESV (`tipuri.GROSIME_ESV_MM`). */
export const LATURA_MIN_CM = 30;
/** Două capete de grindă cu o coordonată mai departe de atât fac grinda
 *  OBLICĂ (`tipuri.TOL_ORTOGONAL_MM`). */
export const TOL_ORTOGONAL_MM = 1;
/** Pasul pe care cade o poziție fără nimic de care să se lipească. */
export const PAS_M = 0.05;

// --- cadrul de desen ---------------------------------------------------------

/** Originea cadrului: colțul din stânga-sus al arhitecturii, în mm. */
export type Cadru = { x0: number; y1: number };

export function cadrulContextului(ctx: ContextEditor, G?: Geometrie): Cadru {
  let x0 = Infinity;
  let y1 = -Infinity;
  for (const n of ctx.niveluri)
    for (const p of n.pereti)
      for (const [x, y] of p.colturi) {
        if (x < x0) x0 = x;
        if (y > y1) y1 = y;
      }
  for (const e of G?.esv ?? []) {
    if (e.nod[0] < x0) x0 = e.nod[0];
    if (e.nod[1] > y1) y1 = e.nod[1];
  }
  if (!Number.isFinite(x0)) return { x0: 0, y1: 0 };
  return { x0, y1 };
}

export const laEcran = (c: Cadru, p: readonly number[]): Pt =>
  [(p[0] - c.x0) / 1000, (c.y1 - p[1]) / 1000];
export const laPlansa = (c: Cadru, p: Pt): Pt =>
  [c.x0 + p[0] * 1000, c.y1 - p[1] * 1000];

// --- citiri -------------------------------------------------------------------

export const nivelurileEsv = (e: Esv): number[] =>
  Object.keys(e.sectiune_per_nivel).map(Number).sort((a, b) => a - b);
export const nivelurileGrinzii = (g: Grinda): number[] =>
  Object.keys(g.sectiune_per_nivel).map(Number).sort((a, b) => a - b);
export const sectiunea = (e: Esv, nivel: number): SectiuneEsv | undefined =>
  e.sectiune_per_nivel[String(nivel)];

/** Versorul axei unui ESV, în milimetrii planșei. */
const versor = (axa: "X" | "Y"): Pt => (axa === "X" ? [1, 0] : [0, 1]);

/** Centrul secțiunii de la `nivel`, în mm: nodul plus alunecarea. */
export function centrulEsv(e: Esv, nivel: number): Pt {
  const s = sectiunea(e, nivel);
  const o = s?.ofs_mm ?? e.ofs_mm ?? 0;
  const u = versor(e.axa);
  return [e.nod[0] + o * u[0], e.nod[1] + o * u[1]];
}

function dreptunghi(c: Pt, u: Pt, L_mm: number, b_mm: number): Pt[] {
  const n: Pt = [-u[1], u[0]];
  const hl = L_mm / 2;
  const hw = b_mm / 2;
  return ([[-1, -1], [1, -1], [1, 1], [-1, 1]] as const).map(([sa, sb]) =>
    [c[0] + sa * u[0] * hl + sb * n[0] * hw,
     c[1] + sa * u[1] * hl + sb * n[1] * hw] as Pt);
}

/** Conturul ESV-ului la `nivel`, în mm ai planșei: piciorul, apoi fiecare
 *  braț, ca poligoane separate (desenul le umple pe toate la fel); o amprentă
 *  purtată din Pasul 4 (`dim_extra.poly_mm`, relativă la nod) iese întreagă. */
export function amprentaEsv(e: Esv, nivel: number): Pt[][] {
  const s = sectiunea(e, nivel);
  if (!s) return [];
  const poly = s.dim_extra?.poly_mm as Pt[] | undefined;
  if (poly && poly.length >= 3)
    return [poly.map(([x, y]) => [e.nod[0] + x, e.nod[1] + y] as Pt)];
  const u = versor(e.axa);
  const v: Pt = [u[1], u[0]];
  const out = [dreptunghi(centrulEsv(e, nivel), u, s.L_cm * 10, s.b_cm * 10)];
  for (const b of s.brate ?? []) {
    if (b.L_cm <= LATURA_MIN_CM + 1e-6) continue;
    const c: Pt = [e.nod[0] + b.la_mm * u[0] + b.ofs_mm * v[0],
                   e.nod[1] + b.la_mm * u[1] + b.ofs_mm * v[1]];
    out.push(dreptunghi(c, v, b.L_cm * 10, b.b_cm * 10));
  }
  return out;
}

/** Cutia secțiunii principale, pe ecran: `{x: [a, b], y: [a, b]}` în metri. */
export type Cutie = { x: [number, number]; y: [number, number] };
export function cutiaEsv(c: Cadru, e: Esv, nivel: number): Cutie | null {
  const s = sectiunea(e, nivel);
  if (!s) return null;
  const pts = dreptunghi(centrulEsv(e, nivel), versor(e.axa),
                         s.L_cm * 10, s.b_cm * 10).map((p) => laEcran(c, p));
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return { x: [Math.min(...xs), Math.max(...xs)], y: [Math.min(...ys), Math.max(...ys)] };
}

/** Capătul unei grinzi, în mm: nodul ESV-ului plus offsetul rigid. */
export function capatulGrinzii(G: Geometrie, g: Grinda, cap: "i" | "j"): Pt | null {
  const e = G.esv.find((x) => x.id === (cap === "i" ? g.nod_i : g.nod_j));
  if (!e) return null;
  const o = (cap === "i" ? g.ofs_i : g.ofs_j) ?? [0, 0];
  return [e.nod[0] + o[0], e.nod[1] + o[1]];
}

/** Grinda iese OBLICĂ: capetele ei nu stau pe același ax. */
export function eOblica(G: Geometrie, g: Grinda): boolean {
  const a = capatulGrinzii(G, g, "i");
  const b = capatulGrinzii(G, g, "j");
  if (!a || !b) return false;
  return Math.min(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1])) > TOL_ORTOGONAL_MM;
}


// --- id-uri --------------------------------------------------------------------

/** Primul număr nefolosit după prefix (ST, GR): id-urile de plan sunt stabile,
 *  deci unul nou nu reia niciodată numărul unui element existent. */
export function idNou(G: Geometrie, prefix: "ST" | "GR"): string {
  const lista = prefix === "ST" ? G.esv : G.grinzi;
  let max = 0;
  for (const x of lista) {
    const m = new RegExp(`^${prefix}(\\d+)$`).exec(x.id);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `${prefix}${max + 1}`;
}

// --- operații (fiecare întoarce o geometrie NOUĂ) ------------------------------

/** Grinzile care pleacă din ESV-urile `ids` își pierd offseturile de capăt,
 *  iar Pasul 4 le reface. Numai la ROTIRE: offsetul e punctul în care grinda
 *  se prinde de element, măsurat din nod — la mutare el pleacă odată cu nodul,
 *  iar la redimensionare rămâne unde era. Golit acolo, o grindă prinsă de o
 *  lamelă departe de nod (GR44 pe ST29, Olteniței P+4) ar sări în nod și ar
 *  ieși oblică. */
function faraOfsuri(grinzi: Grinda[], ids: Set<string>): Grinda[] {
  return grinzi.map((g) => (ids.has(g.nod_i) || ids.has(g.nod_j))
    && (g.ofs_i || g.ofs_j) ? { ...g, ofs_i: null, ofs_j: null } : g);
}

export function adaugaEsv(G: Geometrie, p: {
  nod: Pt; axa: "X" | "Y"; b_cm: number; L_cm: number; niveluri: number[];
}): { G: Geometrie; id: string } {
  const id = idNou(G, "ST");
  const sec: Record<string, SectiuneEsv> = {};
  for (const n of p.niveluri) sec[String(n)] = { b_cm: p.b_cm, L_cm: p.L_cm, ofs_mm: 0 };
  const e: Esv = { id, axa: p.axa, nod: [p.nod[0], p.nod[1]], ofs_mm: 0,
                   sectiune_per_nivel: sec, baza: "fundatie", transfer: [],
                   pereche: null, tip: null };
  return { G: { ...G, esv: [...G.esv, e] }, id };
}

/** Șterge ESV-ul — de tot (`dela == null`), sau de la nivelul `dela` în sus:
 *  un element nu poate lipsi la mijloc, fiindcă ce e deasupra ar sta în aer.
 *  Grinzile rămase fără capăt la un nivel pleacă și ele de acolo. */
export function stergeEsv(G: Geometrie, id: string, dela: number | null = null): Geometrie {
  const esv: Esv[] = [];
  for (const e of G.esv) {
    if (e.id !== id) { esv.push(e); continue; }
    if (dela == null) continue;
    const sec = Object.fromEntries(Object.entries(e.sectiune_per_nivel)
      .filter(([n]) => Number(n) < dela));
    if (Object.keys(sec).length) esv.push({ ...e, sectiune_per_nivel: sec });
  }
  return { ...G, esv, grinzi: curataGrinzile(G.grinzi, esv) };
}

/** Grinzile care n-au ESV la amândouă capetele, pe fiecare nivel. */
function curataGrinzile(grinzi: Grinda[], esv: Esv[]): Grinda[] {
  const prin = new Map(esv.map((e) => [e.id, e]));
  const out: Grinda[] = [];
  for (const g of grinzi) {
    const a = prin.get(g.nod_i);
    const b = prin.get(g.nod_j);
    if (!a || !b) continue;
    const sec = Object.fromEntries(Object.entries(g.sectiune_per_nivel)
      .filter(([n]) => a.sectiune_per_nivel[n] && b.sectiune_per_nivel[n]));
    if (Object.keys(sec).length) out.push({ ...g, sectiune_per_nivel: sec });
  }
  return out;
}

/** Mută NODUL ESV-ului (pe toate nivelurile: e același element de sus până
 *  jos). Secțiunea îl urmează, și capetele grinzilor prinse de el. */
export function mutaEsv(G: Geometrie, id: string, nod: Pt): Geometrie {
  return {
    ...G,
    esv: G.esv.map((e) => (e.id === id ? { ...e, nod: [nod[0], nod[1]] } : e)),
  };
}

/** Secțiunea nouă la nivelul `nivel` — sau pe toate (`nivel == null`).
 *
 *  REGULA VERTICALĂ: o secțiune poate scădea în sus, niciodată crește. Deci
 *  la un singur nivel, ce e deasupra se strânge cel mult la noua mărime, iar
 *  ce e dedesubt crește cel puțin la ea. O formă schimbată își pierde
 *  amprenta purtată din Pasul 4 (ea descria vechea formă); brațele rămân. */
export function redimEsv(G: Geometrie, id: string, nivel: number | null,
                         m: { b_cm?: number; L_cm?: number }): Geometrie {
  const cu = (s: SectiuneEsv, b?: number, L?: number): SectiuneEsv => {
    const { dim_extra: _extra, ...rest } = s;
    void _extra;
    return { ...rest, ...(b != null ? { b_cm: b } : {}), ...(L != null ? { L_cm: L } : {}) };
  };
  return {
    ...G,
    esv: G.esv.map((e) => {
      if (e.id !== id) return e;
      const sec: Record<string, SectiuneEsv> = {};
      for (const [k, s] of Object.entries(e.sectiune_per_nivel)) {
        const n = Number(k);
        if (nivel == null || n === nivel) {
          sec[k] = cu(s, m.b_cm, m.L_cm);
        } else if (n > nivel) {
          sec[k] = cu(s, m.b_cm != null ? Math.min(s.b_cm, m.b_cm) : undefined,
                      m.L_cm != null ? Math.min(s.L_cm, m.L_cm) : undefined);
        } else {
          sec[k] = cu(s, m.b_cm != null ? Math.max(s.b_cm, m.b_cm) : undefined,
                      m.L_cm != null ? Math.max(s.L_cm, m.L_cm) : undefined);
        }
      }
      return { ...e, sectiune_per_nivel: sec };
    }),
  };
}

/** Întoarce ESV-ul pe cealaltă axă. Brațele și amprenta purtată erau ale
 *  vechii orientări, deci pleacă. */
export function rotesteEsv(G: Geometrie, id: string): Geometrie {
  return {
    ...G,
    esv: G.esv.map((e) => {
      if (e.id !== id) return e;
      const sec = Object.fromEntries(Object.entries(e.sectiune_per_nivel)
        .map(([k, s]) => [k, { b_cm: s.b_cm, L_cm: s.L_cm, ofs_mm: 0 }]));
      return { ...e, axa: e.axa === "X" ? "Y" : "X", ofs_mm: 0,
               sectiune_per_nivel: sec };
    }),
    grinzi: faraOfsuri(G.grinzi, new Set([id])),
  };
}

/** Până la ce nivel urcă ESV-ul, de la fundație. Urcarea ia secțiunea de sus
 *  a elementului; coborârea taie ce e deasupra (cu grinzile de acolo). */
export function panaLaNivel(G: Geometrie, id: string, sus: number): Geometrie {
  const e = G.esv.find((x) => x.id === id);
  if (!e) return G;
  const niv = nivelurileEsv(e);
  const top = niv[niv.length - 1];
  if (sus < top) return stergeEsv(G, id, sus + 1);
  const ultima = e.sectiune_per_nivel[String(top)];
  const sec = { ...e.sectiune_per_nivel };
  for (let n = top + 1; n <= sus; n += 1) {
    const { dim_extra: _x, ...rest } = ultima;
    void _x;
    sec[String(n)] = { ...rest };
  }
  return { ...G, esv: G.esv.map((x) => (x.id === id ? { ...x, sectiune_per_nivel: sec } : x)) };
}

/** Offsetul capătului `p` față de nodul elementului: `null` când stă în nod. */
const ofsetul = (p: Pt, e: Esv): Pt | null =>
  (Math.abs(p[0] - e.nod[0]) < 1e-6 && Math.abs(p[1] - e.nod[1]) < 1e-6
    ? null : [p[0] - e.nod[0], p[1] - e.nod[1]]);

/** O grindă nouă între două ESV-uri, pe nivelurile cerute pe care există
 *  amândouă. `null` când n-are niciun nivel comun. `capete` sunt punctele
 *  (mm) în care grinda se prinde de fiecare element — alese de om
 *  (`ancorareaPeEsv`); fără ele, nodurile. */
export function adaugaGrinda(G: Geometrie, a: string, b: string, niveluri: number[],
                             sec: SectiuneGrinda,
                             capete?: { a: Pt; b: Pt }): { G: Geometrie; id: string } | null {
  const ea = G.esv.find((e) => e.id === a);
  const eb = G.esv.find((e) => e.id === b);
  if (!ea || !eb || a === b) return null;
  const comune = niveluri.filter((n) => ea.sectiune_per_nivel[String(n)]
                                      && eb.sectiune_per_nivel[String(n)]);
  if (!comune.length) return null;
  const pa = capete?.a ?? ea.nod;
  const pb = capete?.b ?? eb.nod;
  // capătul i în stânga (jos, pe grinzile transversale) — convenția Pasului 4
  const aInI = pa[0] < pb[0] - TOL_ORTOGONAL_MM
    || (Math.abs(pa[0] - pb[0]) <= TOL_ORTOGONAL_MM && pa[1] <= pb[1]);
  const [i, j, pi, pj, ei, ej] = aInI ? [a, b, pa, pb, ea, eb] : [b, a, pb, pa, eb, ea];
  const id = idNou(G, "GR");
  const g: Grinda = {
    id, nod_i: i, nod_j: j, familie: null, cadru: false,
    ofs_i: ofsetul(pi, ei), ofs_j: ofsetul(pj, ej),
    sectiune_per_nivel: Object.fromEntries(comune.map((n) => [String(n),
      { b_cm: sec.b_cm, h_m: sec.h_m }])),
  };
  return { G: { ...G, grinzi: [...G.grinzi, g] }, id };
}

/** Grinda există deja între cele două ESV-uri la nivelul ăsta? Cu `capete`
 *  date, numai una prinsă în aceleași puncte: pe o lamelă lungă încap două
 *  grinzi spre același vecin, la capete diferite. */
export function grindaIntre(G: Geometrie, a: string, b: string, nivel: number,
                            capete?: { a: Pt; b: Pt }): Grinda | undefined {
  const egal = (p: Pt | null, q: Pt) =>
    !!p && Math.abs(p[0] - q[0]) <= TOL_ORTOGONAL_MM && Math.abs(p[1] - q[1]) <= TOL_ORTOGONAL_MM;
  return G.grinzi.find((g) => {
    if (!g.sectiune_per_nivel[String(nivel)]) return false;
    const drept = g.nod_i === a && g.nod_j === b;
    const invers = g.nod_i === b && g.nod_j === a;
    if (!drept && !invers) return false;
    if (!capete) return true;
    const pi = capatulGrinzii(G, g, "i");
    const pj = capatulGrinzii(G, g, "j");
    return drept ? egal(pi, capete.a) && egal(pj, capete.b)
                 : egal(pi, capete.b) && egal(pj, capete.a);
  });
}

/** Șterge grinda — de tot (`nivel == null`) sau numai de la un nivel. */
export function stergeGrinda(G: Geometrie, id: string, nivel: number | null = null): Geometrie {
  const grinzi: Grinda[] = [];
  for (const g of G.grinzi) {
    if (g.id !== id) { grinzi.push(g); continue; }
    if (nivel == null) continue;
    const sec = Object.fromEntries(Object.entries(g.sectiune_per_nivel)
      .filter(([n]) => Number(n) !== nivel));
    if (Object.keys(sec).length) grinzi.push({ ...g, sectiune_per_nivel: sec });
  }
  return { ...G, grinzi };
}

export function redimGrinda(G: Geometrie, id: string, nivel: number | null,
                            m: { b_cm?: number; h_m?: number }): Geometrie {
  return {
    ...G,
    grinzi: G.grinzi.map((g) => g.id !== id ? g : {
      ...g,
      sectiune_per_nivel: Object.fromEntries(Object.entries(g.sectiune_per_nivel)
        .map(([k, s]) => [k, nivel == null || Number(k) === nivel
          ? { b_cm: m.b_cm ?? s.b_cm, h_m: m.h_m ?? s.h_m } : s])),
    }),
  };
}

/** Secțiunea cea mai des întâlnită a grinzilor unui nivel — cu ea pleacă o
 *  grindă nouă, ca să semene cu vecinele ei (un cofraj, nu două). */
export function sectiuneaObisnuita(G: Geometrie, nivel: number): SectiuneGrinda {
  const nr = new Map<string, number>();
  for (const g of G.grinzi) {
    const s = g.sectiune_per_nivel[String(nivel)];
    if (!s) continue;
    const k = `${s.b_cm}|${s.h_m}`;
    nr.set(k, (nr.get(k) ?? 0) + 1);
  }
  let best = "30|0.5";
  let n = 0;
  for (const [k, v] of nr) if (v > n) { best = k; n = v; }
  const [b, h] = best.split("|").map(Number);
  return { b_cm: b, h_m: h };
}

/** Greșelile pe care editorul le vede singur, ca să le arate pe desen înainte
 *  de calcul (serverul le spune pe toate, v. `tipuri.valideaza`). */
export function greseli(G: Geometrie): { grinzi: Set<string>; mesaje: string[] } {
  const grinzi = new Set<string>();
  const mesaje: string[] = [];
  for (const g of G.grinzi) {
    if (eOblica(G, g)) {
      grinzi.add(g.id);
      mesaje.push(t("struct.editor.msg_grinda_oblica", { id: g.id, i: g.nod_i, j: g.nod_j }));
    }
  }
  return { grinzi, mesaje };
}

// --- lipirea -----------------------------------------------------------------

/** Axa unui perete pe ecran: segmentul median, grosimea și orientarea. */
export type AxaPerete = {
  id: string | null;
  axa: "X" | "Y";
  /** coordonata axei (y pentru un perete pe X, x pentru unul pe Y) */
  cota: number;
  de: number;
  la: number;
  grosime: number;
};

/** Cât se poate abate latura lungă a unui perete de X sau Y ca el să mai aibă
 *  o axă pe care să se lipească un ESV (care stă numai pe X sau pe Y). */
const TOL_OBLIC_GRADE = 2;

/** Axele pereților unui nivel, în metri pe ecran. Un perete e un dreptunghi
 *  (`colturi` de 4); latura lungă îi dă orientarea. Un perete OBLIC față de
 *  axele clădirii n-are axă: cutia lui ar da una pe diagonala zidului. */
export function axelePeretilor(c: Cadru, n: NivelContext): AxaPerete[] {
  const out: AxaPerete[] = [];
  for (const p of n.pereti) {
    const pts = p.colturi.map((q) => laEcran(c, q));
    if (pts.length < 3) continue;
    let lung = 0, unghi = 0;
    pts.forEach((q, i) => {
      const r = pts[(i + 1) % pts.length];
      const d = Math.hypot(r[0] - q[0], r[1] - q[1]);
      if (d > lung) { lung = d; unghi = Math.atan2(r[1] - q[1], r[0] - q[0]); }
    });
    const abatere = Math.abs(((unghi * 180) / Math.PI) % 90);
    if (Math.min(abatere, 90 - abatere) > TOL_OBLIC_GRADE) continue;
    const xs = pts.map((q) => q[0]);
    const ys = pts.map((q) => q[1]);
    const x0 = Math.min(...xs), x1 = Math.max(...xs);
    const y0 = Math.min(...ys), y1 = Math.max(...ys);
    if (x1 - x0 >= y1 - y0) {
      out.push({ id: p.id, axa: "X", cota: (y0 + y1) / 2, de: x0, la: x1, grosime: y1 - y0 });
    } else {
      out.push({ id: p.id, axa: "Y", cota: (x0 + x1) / 2, de: y0, la: y1, grosime: x1 - x0 });
    }
  }
  return out;
}

/** O linie de ghidaj pe ecran: coordonata `axa` (0 = x, 1 = y) la `valoare`. */
export type Ghidaj = { axa: 0 | 1; valoare: number };

/** Ce a găsit lipirea: punctul, axa zidului în care a căzut (dacă a căzut
 *  într-unul) și liniile de ghidaj de desenat. */
export type Lipire = {
  p: Pt;
  perete: AxaPerete | null;
  ghidaje: Ghidaj[];
};

/** Axele arhitectului ca ghidaje pe ecran: numai cele pe X sau pe Y ale
 *  clădirii (alinierea le îndreaptă pe grilă; una rămasă oblică nu e o
 *  coordonată de care să se lipească un nod). */
export function ghidajeleAxelor(c: Cadru, axe: readonly AxaArh[]): Ghidaj[] {
  const out: Ghidaj[] = [];
  for (const ax of axe) {
    const a = laEcran(c, ax.a), b = laEcran(c, ax.b);
    if (Math.abs(a[0] - b[0]) <= 1e-3) out.push({ axa: 0, valoare: a[0] });
    else if (Math.abs(a[1] - b[1]) <= 1e-3) out.push({ axa: 1, valoare: a[1] });
  }
  return out;
}

/** UNDE CADE UN NOD tras cu mâna.
 *
 *  Două reguli, în ordinea asta, ca la un structurist cu creionul:
 *
 *    1. ÎN ZID. Un nod care cade în interiorul unui perete (sau la mai puțin de
 *       `tol` de el) sare pe AXA peretelui — elementul stă în zid, centrat pe
 *       grosimea lui — și rămâne între capetele lui;
 *    2. AX PE AX. Pe coordonata rămasă liberă (de-a lungul zidului, sau pe
 *       amândouă când nodul e în afara zidurilor), nodul se aliniază cu nodul
 *       altui ESV sau cu un ax al arhitectului (`ghidaje`) aflat la mai puțin
 *       de `tolAx`: grinda dintre ele iese dreaptă, iar axele clădirii rămân
 *       puține. Fără vecin, poziția cade pe `PAS_M`.
 *
 *  `noduri` sunt nodurile celorlalte ESV-uri, pe ecran (fără cel mutat, ca să
 *  nu se lipească de el însuși); `tol` e raza zidului, `tolAx` a axelor. */
export function lipesteNodul(p: Pt, pereti: AxaPerete[], noduri: Pt[], tol: number,
                             tolAx: number = tol, ghidajeAxe: readonly Ghidaj[] = []): Lipire {
  let perete: AxaPerete | null = null;
  let dMin = Infinity;
  for (const a of pereti) {
    const [lung, trans] = a.axa === "X" ? [p[0], p[1]] : [p[1], p[0]];
    if (lung < a.de - tol || lung > a.la + tol) continue;
    const d = Math.abs(trans - a.cota) - a.grosime / 2;
    if (d <= tol && d < dMin) { dMin = d; perete = a; }
  }
  const ghidaje: Lipire["ghidaje"] = [];
  const rot = (v: number) => Math.round(v / PAS_M) * PAS_M;
  const peAxa = (axa: 0 | 1, v: number): number => {
    let best: number | null = null;
    let d = tolAx;
    for (const q of noduri) {
      const dd = Math.abs(q[axa] - v);
      if (dd <= d) { d = dd; best = q[axa]; }
    }
    for (const gh of ghidajeAxe) {
      if (gh.axa !== axa) continue;
      const dd = Math.abs(gh.valoare - v);
      if (dd <= d) { d = dd; best = gh.valoare; }
    }
    if (best != null) { ghidaje.push({ axa, valoare: best }); return best; }
    return rot(v);
  };
  if (perete) {
    // pe grosimea zidului: axa pe care stau deja nodurile din el, dacă stau
    // pe una — altfel mijlocul zidului. Nodurile unui zid au o axă comună,
    // chiar când ea nu e chiar mijlocul lui (un etaj retras păstrează grila
    // de jos), iar o grindă între ele trebuie să iasă dreaptă.
    const tr: 0 | 1 = perete.axa === "X" ? 1 : 0;
    const lg: 0 | 1 = tr === 1 ? 0 : 1;
    const pe = noduri.filter((q) => Math.abs(q[tr] - perete!.cota) <= perete!.grosime / 2 + 1e-6
      && q[lg] >= perete!.de - 1e-6 && q[lg] <= perete!.la + 1e-6);
    const cota = pe.length
      ? pe.reduce((b, q) => (Math.abs(q[tr] - p[tr]) < Math.abs(b - p[tr]) ? q[tr] : b), pe[0][tr])
      : perete.cota;
    if (pe.length && Math.abs(cota - perete.cota) > 1e-6) ghidaje.push({ axa: tr, valoare: cota });
    const v = Math.min(perete.la, Math.max(perete.de, peAxa(lg, p[lg])));
    return { p: lg === 0 ? [v, cota] : [cota, v], perete, ghidaje };
  }
  return { p: [peAxa(0, p[0]), peAxa(1, p[1])], perete: null, ghidaje };
}

// --- grinzile alunecă pe element ---------------------------------------------

/** Axele pereților unui nivel în MILIMETRII planșei (cu Y-ul în sus), nu pe
 *  ecran: aceleași ca `axelePeretilor`, întoarse înapoi. */
export function axelePeretilorMm(n: NivelContext): AxaPerete[] {
  return axelePeretilor({ x0: 0, y1: 0 }, n).map((a) => (a.axa === "X"
    ? { ...a, cota: -a.cota * 1000, de: a.de * 1000, la: a.la * 1000, grosime: a.grosime * 1000 }
    : { ...a, cota: a.cota * 1000, de: -a.la * 1000, la: -a.de * 1000,
        grosime: a.grosime * 1000 }));
}

/** Între ce coordonate, pe direcția `tr`, poate sta capătul unei grinzi pe
 *  ESV-ul `e` la `nivel`: întinderea betonului care trece prin coordonata
 *  `lung` a capătului (piesele care nu trec pe acolo nu-l pot primi). */
function plajaPeEsv(e: Esv, nivel: number, tr: 0 | 1, lung: number): [number, number] | null {
  const bucati = amprentaEsv(e, nivel);
  if (!bucati.length) return null;
  const lg = tr === 0 ? 1 : 0;
  const cutii = bucati.map((pts) => {
    const a = pts.map((q) => q[lg]);
    const b = pts.map((q) => q[tr]);
    return { l: [Math.min(...a), Math.max(...a)], t: [Math.min(...b), Math.max(...b)] };
  });
  const prin = cutii.filter((c) => lung >= c.l[0] - 1 && lung <= c.l[1] + 1);
  const din = prin.length ? prin : cutii;
  return [Math.min(...din.map((c) => c.t[0])), Math.max(...din.map((c) => c.t[1]))];
}

/** GRINZILE ALUNECĂ PE ELEMENT. După ce un ESV s-a mutat, redimensionat sau
 *  rotit (`ids`), fiecare grindă prinsă de el își caută din nou linia —
 *  direcția ei rămâne cea din geometria de dinainte (`ref`), iar capetele
 *  își schimbă doar componenta de-a curmezișul a offsetului:
 *
 *    1. axa grinzii trebuie să cadă în betonul AMBELOR capete — altfel ar
 *       ieși oblică sau pe lângă element. Axa, nu toată lățimea: Pasul 4
 *       prinde grinzile și în vârful unei lamele, cu nodul acolo;
 *    2. dintre liniile care încap, cea care stă cel mai mult ÎN ZIDURILE
 *       arhitecturii de pe direcția ei, pe toate nivelurile grinzii: linia
 *       de dinainte, dacă e deja într-un zid, sau axa unui zid; la egalitate,
 *       cea mai apropiată de linia de dinainte;
 *    3. fără zid, linia de dinainte, adusă în plajă.
 *
 *  Când betonul celor două capete nu se mai privește deloc, grinda rămâne cum
 *  a lăsat-o operația — oblică, și semnalată ca atare (`greseli`). */
export function aseazaGrinzile(G: Geometrie, ids: Iterable<string>,
                               ctx: ContextEditor | null, ref: Geometrie): Geometrie {
  const atinse = new Set(ids);
  if (!atinse.size) return G;
  const prin = new Map(G.esv.map((e) => [e.id, e]));
  const vechi = new Map(ref.grinzi.map((g) => [g.id, g]));
  const axe = new Map<number, AxaPerete[]>();
  const axeLa = (n: number): AxaPerete[] => {
    if (!axe.has(n)) {
      const nc = ctx?.niveluri.find((x) => x.index === n);
      axe.set(n, nc ? axelePeretilorMm(nc) : []);
    }
    return axe.get(n)!;
  };
  let schimbat = false;
  const grinzi = G.grinzi.map((g) => {
    if (!atinse.has(g.nod_i) && !atinse.has(g.nod_j)) return g;
    const a = prin.get(g.nod_i);
    const b = prin.get(g.nod_j);
    const g0 = vechi.get(g.id) ?? g;
    const pa0 = capatulGrinzii(ref, g0, "i");
    const pb0 = capatulGrinzii(ref, g0, "j");
    if (!a || !b || !pa0 || !pb0) return g;
    const lg: 0 | 1 = Math.abs(pb0[0] - pa0[0]) >= Math.abs(pb0[1] - pa0[1]) ? 0 : 1;
    const tr: 0 | 1 = lg === 0 ? 1 : 0;
    const c0 = (pa0[tr] + pb0[tr]) / 2;
    const oi: Pt = [...(g.ofs_i ?? [0, 0])] as Pt;
    const oj: Pt = [...(g.ofs_j ?? [0, 0])] as Pt;
    const la = a.nod[lg] + oi[lg];
    const lb = b.nod[lg] + oj[lg];
    const niv = nivelurileGrinzii(g);
    let lo = -Infinity, hi = Infinity;
    for (const n of niv) {
      for (const [e, lung] of [[a, la], [b, lb]] as const) {
        const r = plajaPeEsv(e, n, tr, lung);
        if (!r) continue;
        lo = Math.max(lo, r[0]);
        hi = Math.min(hi, r[1]);
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi + TOL_ORTOGONAL_MM) return g;
    if (lo > hi) lo = hi = (lo + hi) / 2;
    const [s0, s1] = [Math.min(la, lb), Math.max(la, lb)];
    const directie = lg === 0 ? "X" : "Y";
    /** Cât din deschiderea grinzii stă, la linia `c`, într-un zid de pe
     *  direcția ei, adunat pe nivelurile grinzii. */
    const inZid = (c: number) => niv.reduce((sum, n) => sum + axeLa(n)
      .filter((w) => w.axa === directie && Math.abs(c - w.cota) <= w.grosime / 2 + 1e-6)
      .reduce((t, w) => t + Math.max(0, Math.min(s1, w.la) - Math.max(s0, w.de)), 0), 0);
    const clamp = (c: number) => Math.min(hi, Math.max(lo, c));
    const candidati = [clamp(c0), ...niv.flatMap((n) => axeLa(n)
      .filter((w) => w.axa === directie && w.cota >= lo - 1e-6 && w.cota <= hi + 1e-6)
      .map((w) => clamp(w.cota)))];
    let c = candidati[0];
    let bun = inZid(c);
    for (const x of candidati.slice(1)) {
      const v = inZid(x);
      if (v > bun + 1e-6 || (Math.abs(v - bun) <= 1e-6 && Math.abs(x - c0) < Math.abs(c - c0))) {
        c = x; bun = v;
      }
    }
    oi[tr] = c - a.nod[tr];
    oj[tr] = c - b.nod[tr];
    const curat = (o: Pt): Pt | null =>
      (Math.abs(o[0]) < 1e-6 && Math.abs(o[1]) < 1e-6 ? null : [o[0], o[1]]);
    const ni = curat(oi), nj = curat(oj);
    const la0 = g.ofs_i ?? null, lb0 = g.ofs_j ?? null;
    const egal = (p: Pt | null, q: Pt | null) => (p == null && q == null)
      || (!!p && !!q && Math.abs(p[0] - q[0]) < 1e-6 && Math.abs(p[1] - q[1]) < 1e-6);
    if (egal(ni, la0) && egal(nj, lb0)) return g;
    schimbat = true;
    return { ...g, ofs_i: ni, ofs_j: nj };
  });
  return schimbat ? { ...G, grinzi } : G;
}

// --- capătul unei grinzi, mutat de mână -----------------------------------------

/** ESV-ul nivelului `nivel` sub punctul `p` (mm): cel a cărui amprentă îl
 *  cuprinde, altfel cel mai apropiat până la `tol_mm`; `fara` nu se ia în
 *  seamă. Întoarce și piesa (piciorul sau brațul) în care cade. */
export function esvSubPunct(G: Geometrie, p: Pt, nivel: number, tol_mm: number,
                            fara: string | null = null)
    : { esv: Esv; piesa: Pt[]; piesaIdx: number } | null {
  let best: { esv: Esv; piesa: Pt[]; piesaIdx: number } | null = null;
  let dMin = Infinity;
  for (const e of G.esv) {
    if (e.id === fara) continue;
    amprentaEsv(e, nivel).forEach((pts, i) => {
      const d = distantaLaCutie(cutiaPiesei(pts), p);
      if (d <= tol_mm && d < dMin) { best = { esv: e, piesa: pts, piesaIdx: i }; dMin = d; }
    });
  }
  return best;
}

/** Piesa ESV-ului `e` în care cade punctul `p` (sau cea mai apropiată de el):
 *  indexul ei în `amprentaEsv`; `null` fără secțiune la nivelul ăsta. */
export function piesaSubPunct(e: Esv, nivel: number, p: Pt): number | null {
  let best: number | null = null;
  let dMin = Infinity;
  amprentaEsv(e, nivel).forEach((pts, i) => {
    const d = distantaLaCutie(cutiaPiesei(pts), p);
    if (d < dMin) { best = i; dMin = d; }
  });
  return best;
}

export type Reancorare =
  | { G: Geometrie; esv: string; ancora: Ancorare | null; linie: LinieIntreEsv | null;
      oblica: boolean; liniePosibila: boolean }
  | { eroare: "fara_element" | "acelasi" | "niveluri"; esv?: string; lipsa?: number[] };

/** CAPĂTUL `cap` AL GRINZII `gid`, MUTAT ÎN PUNCTUL `p` (mm), la `nivel`.
 *
 *  Capătul se prinde de ESV-ul de sub punct — și de cel pe care stă deja,
 *  ca să treacă pe altă piesă a lui — pe LINIA COMUNĂ a piesei de sub punct
 *  cu piesa celuilalt capăt (`liniaAleasa`): linia capătului ăluia, dacă
 *  betonul de aici o acoperă — atunci el nu se mișcă —, altfel una nouă, pe
 *  care capătul celălalt alunecă în piesa lui. Cu `liber` (Alt), capătul
 *  cade unde l-a lăsat omul — pe punctul de ancorare din apropiere, dacă e
 *  unul la `tol_mm`, altfel liber în beton (`ancorareaPeEsv`) —, iar
 *  celălalt nu se mișcă: dacă cele două nu mai stau pe același ax, grinda
 *  iese oblică și e semnalată (`greseli`), nu refuzată. La fel fără nicio
 *  linie comună.
 *  `oblica` spune că a ieșit așa, iar `liniePosibila` dacă elementul are
 *  vreun punct pe linia celuilalt capăt: fără el, grinda nu se poate
 *  îndrepta de la capătul ăsta oricum ar fi tras — betonul elementului nu
 *  acoperă linia, deci trebuie mutat elementul sau celălalt capăt.
 *
 *  Elementul nou trebuie să existe pe toate nivelurile grinzii: o grindă nu
 *  poate rămâne fără reazem pe un nivel. Capetele se reordonează după
 *  convenția Pasului 4 (i în stânga, jos pe cele transversale). */
export function reancoreazaCapatul(G: Geometrie, gid: string, cap: "i" | "j", p: Pt,
                                   nivel: number, tol_mm: number,
                                   axe: readonly AxaArh[] = [], liber = false): Reancorare {
  const g = G.grinzi.find((x) => x.id === gid);
  if (!g) return { eroare: "fara_element" };
  const idAlt = cap === "i" ? g.nod_j : g.nod_i;
  const alt = G.esv.find((x) => x.id === idAlt);
  const q = capatulGrinzii(G, g, cap === "i" ? "j" : "i");
  if (!alt || !q) return { eroare: "fara_element" };
  const sub = esvSubPunct(G, p, nivel, tol_mm);
  if (!sub) return { eroare: "fara_element" };
  const e = sub.esv;
  if (e.id === idAlt) return { eroare: "acelasi", esv: e.id };
  const niv = nivelurileGrinzii(g);
  const lipsa = niv.filter((n) => !e.sectiune_per_nivel[String(n)]);
  if (lipsa.length) return { eroare: "niveluri", esv: e.id, lipsa };

  const b_mm = 10 * (g.sectiune_per_nivel[String(nivel)]?.b_cm ?? LATIME_GRINDA_MM / 10);
  const linie = liber ? null
    : liniaAleasa(alt, piesaSubPunct(alt, nivel, q), e, sub.piesaIdx, nivel, p, axe, q, b_mm);
  const anc = linie ? null : ancorareaPeEsv(e, nivel, p, tol_mm, q, axe);
  if (!linie && !anc) return { eroare: "fara_element" };
  const pNou: Pt = linie ? linie.b : anc!.p;
  // pe linia capătului celălalt el rămâne unde era; pe o linie nouă alunecă
  const qNou: Pt = linie && linie.prin !== "capat" ? linie.a : q;
  const ofsNou = ofsetul(pNou, e);
  const ofsAlt = ofsetul(qNou, alt);
  // convenția Pasului 4: i în stânga, jos pe grinzile transversale
  const nouEI = pNou[0] < qNou[0] - TOL_ORTOGONAL_MM
    || (Math.abs(pNou[0] - qNou[0]) <= TOL_ORTOGONAL_MM && pNou[1] <= qNou[1]);
  const nou: Grinda = nouEI
    ? { ...g, nod_i: e.id, ofs_i: ofsNou, nod_j: alt.id, ofs_j: ofsAlt }
    : { ...g, nod_i: alt.id, ofs_i: ofsAlt, nod_j: e.id, ofs_j: ofsNou };
  const G1 = { ...G, grinzi: G.grinzi.map((x) => (x.id === gid ? nou : x)) };
  return { G: G1, esv: e.id, ancora: anc, linie, oblica: eOblica(G1, nou),
           liniePosibila: !!linie || !!anc?.puncte.some((c) => c.fel === "linie") };
}

// --- punctele de ancorare ale unei grinzi pe un ESV -----------------------------

/** Un punct în care capătul unei grinzi se prinde de obicei de un ESV. */
export type PunctAncorare = {
  p: Pt;
  /** `nod`: nodul elementului; `centru`: mijlocul unei piese; `capat`:
   *  mijlocul unei fețe a piesei (capetele lamelei, fețele stâlpului);
   *  `ax`: unde o axă a arhitectului trece prin piesă — lamela trece de obicei
   *  de axul zidului perpendicular cu jumătate din grosimea lui, deci fața ei
   *  nu e pe ax; `linie`: locul din dreptul celuilalt capăt al grinzii, pe
   *  linia lui — de acolo grinda iese ortogonală, deci e cel RECOMANDAT */
  fel: "nod" | "centru" | "capat" | "ax" | "linie";
};

/** O axă a arhitectului, ca segment în mm ai planșei (`NivelContext.axe`). */
export type AxaArh = { a: Pt; b: Pt };

/** Unde segmentul axei `ax` taie segmentul `[p0, p1]` (linia mediană a unei
 *  piese), cu 1 mm de toleranță la capete; `null` dacă sunt paralele sau nu
 *  se ating. */
function taieAxa(ax: AxaArh, p0: Pt, p1: Pt): Pt | null {
  const r: Pt = [ax.b[0] - ax.a[0], ax.b[1] - ax.a[1]];
  const s: Pt = [p1[0] - p0[0], p1[1] - p0[1]];
  const cr = r[0] * s[1] - r[1] * s[0];
  const lr = Math.hypot(...r), ls = Math.hypot(...s);
  if (lr < 1e-6 || ls < 1e-6 || Math.abs(cr) < 1e-9 * lr * ls) return null;
  const w: Pt = [p0[0] - ax.a[0], p0[1] - ax.a[1]];
  const t = (w[0] * s[1] - w[1] * s[0]) / cr;   // pe axă
  const u = (w[0] * r[1] - w[1] * r[0]) / cr;   // pe mediană
  if (t * lr < -1 || t * lr > lr + 1 || u * ls < -1 || u * ls > ls + 1) return null;
  return [Math.round(ax.a[0] + t * r[0]), Math.round(ax.a[1] + t * r[1])];
}

/** Dreptunghiul unei piese, pe axe: `[[x0, x1], [y0, y1]]`. */
export type CutiePiesa = [[number, number], [number, number]];
const cutiaPiesei = (pts: Pt[]): CutiePiesa => {
  const xs = pts.map((v) => v[0]);
  const ys = pts.map((v) => v[1]);
  return [[Math.min(...xs), Math.max(...xs)], [Math.min(...ys), Math.max(...ys)]];
};
/** Distanța de la `p` la cutie: zero înăuntru. */
const distantaLaCutie = (c: CutiePiesa, p: Pt): number =>
  Math.hypot(Math.max(c[0][0] - p[0], 0, p[0] - c[0][1]),
             Math.max(c[1][0] - p[1], 0, p[1] - c[1][1]));

// --- liniile de ancorare: grinda paralelă sau perpendiculară pe piesă ----------
//
// Structuristul nu alege un PUNCT pe element, ci o LINIE: grinda continuă
// lamela — pe mediana ei, prinsă în vârf, ca betonul și barele ei să meargă
// prin tot elementul până în capăt — sau intră în ea prin fața lungă,
// perpendicular, prinsă în mijlocul piesei. Între două piese dreptunghiulare
// pe axe există cel mult o asemenea linie pe fiecare direcție: betonul lor
// trebuie să se privească de-a curmezișul grinzii, iar pe direcția ei piesele
// să stea una după alta. Capetele se socotesc PE linie, deci grinda iese
// dreaptă prin construcție, în orice ordine ar veni clicurile; oblică rămâne
// numai când nicio linie comună nu există, sau când omul ancorează liber.

/** O linie pe care stă o grindă dreaptă între două piese. */
export type LinieAncorare = {
  /** direcția grinzii: 0 = pe X, 1 = pe Y */
  lg: 0 | 1;
  /** coordonata de-a curmezișul grinzii (y pentru una pe X) */
  c: number;
  /** capetele, pe prima și pe a doua piesă (mm) */
  a: Pt;
  b: Pt;
  /** de unde vine coordonata: linia capătului deja ancorat, un ax al
   *  arhitectului pe direcția grinzii, mediana unei lamele în lungul grinzii,
   *  centrul unei piese de-a curmezișul ei sau mijlocul betonului comun */
  prin: "capat" | "ax" | "mediana" | "centru" | "mijloc";
};

/** Lățimea grinzii obișnuite (mm), pentru cine n-are una la îndemână. O
 *  linie comună cere beton comun cât TOATĂ lățimea grinzii, de-a curmezișul
 *  ei: grinda stă cu toată lățimea pe fiecare element, altfel nu e grindă —
 *  nu se fac grinzi pe jumătate în ESV. Fără pragul ăsta o suprapunere de un
 *  milimetru (lamela care se oprește la fața zidului vecin, în dreptul
 *  lamelei de pe el) dădea o «linie» pe colțul uneia și pe fața celeilalte. */
export const LATIME_GRINDA_MM = 300;

/** Piesa stă în LUNGUL grinzii (o lamelă pe care grinda o continuă), nu de-a
 *  curmezișul ei; stâlpul pătrat e de-a curmezișul: grinda se prinde în
 *  mijlocul lui. */
const inLungul = (cut: CutiePiesa, lg: 0 | 1): boolean =>
  cut[lg][1] - cut[lg][0] > cut[lg === 0 ? 1 : 0][1] - cut[lg === 0 ? 1 : 0][0] + 1e-6;

/** Capătul unei piese pe linia `(lg, c)`: vârful dinspre cealaltă piesă
 *  (`spre`) când piesa stă în lungul grinzii, mijlocul ei când stă de-a
 *  curmezișul. Rotunjit la zecimea de milimetru. */
function capatulPiesei(cut: CutiePiesa, lg: 0 | 1, c: number, spre: 1 | -1): Pt {
  const v = inLungul(cut, lg) ? (spre > 0 ? cut[lg][1] : cut[lg][0])
    : (cut[lg][0] + cut[lg][1]) / 2;
  const r = (x: number) => Math.round(x * 10) / 10;
  return lg === 0 ? [r(v), r(c)] : [r(c), r(v)];
}

/** LINIA COMUNĂ a două piese, dacă există: betonul lor comun de-a curmezișul
 *  grinzii ține toată lățimea ei (`b_mm`), iar axa stă la cel puțin o
 *  jumătate de lățime de marginile lui. `prefer` e capătul deja ancorat al
 *  unei grinzi: linia lui câștigă dacă grinda încape pe ea. Altfel, în
 *  ordine: un ax al arhitectului pe direcția grinzii, prin ambele piese;
 *  mediana unei lamele în lungul grinzii (a celei mai lungi, când sunt două);
 *  centrul unei piese de-a curmezișul; mijlocul betonului comun. */
export function liniaIntrePiese(A: CutiePiesa, B: CutiePiesa, axe: readonly AxaArh[] = [],
                                prefer: Pt | null = null,
                                b_mm: number = LATIME_GRINDA_MM): LinieAncorare | null {
  for (const lg of [0, 1] as const) {
    const tr: 0 | 1 = lg === 0 ? 1 : 0;
    const lo = Math.max(A[tr][0], B[tr][0]);
    const hi = Math.min(A[tr][1], B[tr][1]);
    if (hi - lo < b_mm - TOL_ORTOGONAL_MM) continue;     // grinda n-ar sta cu toată lățimea
    let spreB: 1 | -1;
    if (A[lg][1] <= B[lg][0] + TOL_ORTOGONAL_MM) spreB = 1;
    else if (B[lg][1] <= A[lg][0] + TOL_ORTOGONAL_MM) spreB = -1;
    else continue;                                       // se încalecă pe direcția grinzii
    // axa grinzii: cu toată lățimea în betonul comun
    const jos = lo + b_mm / 2, sus = hi - b_mm / 2;
    const cuprins = (v: number) => v >= jos - TOL_ORTOGONAL_MM && v <= sus + TOL_ORTOGONAL_MM;
    const l0 = Math.min(A[lg][0], B[lg][0]), l1 = Math.max(A[lg][1], B[lg][1]);
    let c: number | null = null;
    let prin: LinieAncorare["prin"] = "mijloc";
    if (prefer && cuprins(prefer[tr])) { c = prefer[tr]; prin = "capat"; }
    if (c == null) {
      for (const ax of axe) {
        if (Math.abs(ax.a[tr] - ax.b[tr]) > 1e-6) continue;  // nu e pe direcția grinzii
        const s0 = Math.min(ax.a[lg], ax.b[lg]), s1 = Math.max(ax.a[lg], ax.b[lg]);
        if (cuprins(ax.a[tr]) && s0 <= l1 + 1 && s1 >= l0 - 1) { c = ax.a[tr]; prin = "ax"; break; }
      }
    }
    if (c == null) {
      const lamele = [A, B].filter((k) => inLungul(k, lg))
        .sort((p, q) => (q[lg][1] - q[lg][0]) - (p[lg][1] - p[lg][0]));
      for (const k of lamele) {
        const v = (k[tr][0] + k[tr][1]) / 2;
        if (cuprins(v)) { c = v; prin = "mediana"; break; }
      }
    }
    if (c == null) {
      for (const k of [A, B]) {
        const v = (k[tr][0] + k[tr][1]) / 2;
        if (cuprins(v)) { c = v; prin = "centru"; break; }
      }
    }
    if (c == null) { c = (lo + hi) / 2; prin = "mijloc"; }
    return { lg, c, a: capatulPiesei(A, lg, c, spreB), b: capatulPiesei(B, lg, c, spreB > 0 ? -1 : 1), prin };
  }
  return null;
}

/** O linie comună a două ELEMENTE, cu piesele ei. */
export type LinieIntreEsv = LinieAncorare & { piesaA: number; piesaB: number };

/** Liniile comune ale elementelor `ea` și `eb` la `nivel`, pe fiecare pereche
 *  de piese care are una (un dreptunghi cu un L: până la două, câte una pe
 *  braț). Cu `piesaA` / `piesaB` date, numai perechile cu piesele alea. */
export function liniileIntre(ea: Esv, eb: Esv, nivel: number, axe: readonly AxaArh[] = [],
                             piesaA: number | null = null, piesaB: number | null = null,
                             prefer: Pt | null = null, b_mm: number = LATIME_GRINDA_MM): LinieIntreEsv[] {
  const out: LinieIntreEsv[] = [];
  const cutiiB = amprentaEsv(eb, nivel).map(cutiaPiesei);
  amprentaEsv(ea, nivel).map(cutiaPiesei).forEach((A, i) => {
    if (piesaA != null && i !== piesaA) return;
    cutiiB.forEach((B, j) => {
      if (piesaB != null && j !== piesaB) return;
      const l = liniaIntrePiese(A, B, axe, prefer, b_mm);
      if (l) out.push({ ...l, piesaA: i, piesaB: j });
    });
  });
  return out;
}

/** Linia pe care editorul pune grinda de la piesa `piesaA` a lui `ea` la
 *  elementul `eb`, la clicul în `p` pe piesa `piesaB` a lui: a perechii alese,
 *  dacă are una; altfel, dintre toate liniile celor două elemente, cea cu
 *  capătul cel mai aproape de clic. `null` când elementele n-au nicio linie
 *  comună. */
export function liniaAleasa(ea: Esv, piesaA: number | null, eb: Esv, piesaB: number | null,
                            nivel: number, p: Pt, axe: readonly AxaArh[] = [],
                            prefer: Pt | null = null, b_mm: number = LATIME_GRINDA_MM): LinieIntreEsv | null {
  const direct = liniileIntre(ea, eb, nivel, axe, piesaA, piesaB, prefer, b_mm);
  if (direct.length) return direct[0];
  const toate = liniileIntre(ea, eb, nivel, axe, null, null, prefer, b_mm);
  if (!toate.length) return null;
  const d = (l: LinieIntreEsv) => Math.hypot(l.b[0] - p[0], l.b[1] - p[1]);
  return toate.reduce((best, l) => (d(l) < d(best) ? l : best));
}

/** Cu cât lipsește betonul ca `ea` și `eb` să aibă o linie comună: pe fiecare
 *  pereche de piese așezate una după alta pe o direcție, cât mai trebuie
 *  suprapunerii cutiilor lor de-a curmezișul ei până la lățimea grinzii
 *  (`b_mm`); cel mai mic, cu direcția grinzii care ar ieși. `null` când nicio
 *  pereche nu stă una după alta (se încalecă). */
export function golulDeLinie(ea: Esv, eb: Esv, nivel: number, b_mm: number = LATIME_GRINDA_MM)
    : { axa: "X" | "Y"; mm: number } | null {
  let best: { axa: "X" | "Y"; mm: number } | null = null;
  const cutiiB = amprentaEsv(eb, nivel).map(cutiaPiesei);
  for (const A of amprentaEsv(ea, nivel).map(cutiaPiesei)) {
    for (const B of cutiiB) {
      for (const lg of [0, 1] as const) {
        const tr: 0 | 1 = lg === 0 ? 1 : 0;
        const dupa = A[lg][1] <= B[lg][0] + TOL_ORTOGONAL_MM || B[lg][1] <= A[lg][0] + TOL_ORTOGONAL_MM;
        if (!dupa) continue;
        const comun = Math.min(A[tr][1], B[tr][1]) - Math.max(A[tr][0], B[tr][0]);
        const mm = Math.max(0, Math.round(b_mm - comun));
        if (!best || mm < best.mm) best = { axa: lg === 0 ? "X" : "Y", mm };
      }
    }
  }
  return best;
}

/** De unde pleacă grinda nouă: elementul, piesa de pe care a dat omul clic și,
 *  cu Alt, punctul exact lăsat de el (altfel `null`: capătul vine pe linie). */
export type CapatDePlecare = { id: string; piesa: number | null; p: Pt | null };

/** CAPETELE GRINZII NOI de la `de` (pe `ea`) la clicul `p` pe piesa `piesaB`
 *  a lui `eb`: pe linia comună a pieselor când există și omul n-a cerut
 *  altceva; cu un capăt fixat de om (`de.p`, sau `liber` acum — Alt, punctul
 *  lăsat de el, lipit de punctele de ancorare din apropiere), celălalt vine
 *  pe linia lui dacă betonul o acoperă (`ancorareaPeEsv`, `puncteDeAncorare`),
 *  altfel grinda iese oblică și se semnalează. */
export function capeteleGrinziiNoi(ea: Esv, de: CapatDePlecare, eb: Esv, piesaB: number | null,
                                   nivel: number, p: Pt, tol_mm: number,
                                   axe: readonly AxaArh[] = [], liber = false,
                                   b_mm: number = LATIME_GRINDA_MM)
    : { a: Pt; b: Pt; linie: LinieIntreEsv | null } | null {
  const cutii = amprentaEsv(ea, nivel).map(cutiaPiesei);
  if (!cutii.length || !amprentaEsv(eb, nivel).length) return null;
  if (!de.p && !liber) {
    const linie = liniaAleasa(ea, de.piesa, eb, piesaB, nivel, p, axe, null, b_mm);
    if (linie) return { a: linie.a, b: linie.b, linie };
  }
  // capătul de pe B: unde l-a lăsat omul (lipit de un punct de ancorare
  // din apropiere, altfel liber), sau pe linia capătului de pe A deja fixat
  const b = ancorareaPeEsv(eb, nivel, p, tol_mm, de.p, axe)!.p;
  if (de.p) return { a: de.p, b, linie: null };
  // capătul de pe A: în dreptul lui b, pe piesa de plecare (sau pe oricare),
  // dacă linia lui trece prin beton; altfel în mijlocul piesei
  const cut = cutii[de.piesa ?? 0] ?? cutii[0];
  const linii = puncteDeAncorare(ea, nivel, b, axe).filter((c) => c.fel === "linie");
  const peLinie = linii.find((c) => distantaLaCutie(cut, c.p) <= TOL_ORTOGONAL_MM) ?? linii[0];
  const centru: Pt = [(cut[0][0] + cut[0][1]) / 2, (cut[1][0] + cut[1][1]) / 2];
  const a = peLinie ? peLinie.p : ancorareaPeEsv(ea, nivel, centru, tol_mm, null, axe)!.p;
  return { a, b, linie: null };
}

/** PUNCTELE DE ANCORARE ale ESV-ului `e` la `nivel`: nodul, apoi pe fiecare
 *  piesă (piciorul, brațele) mijlocul ei și mijlocul fiecărei fețe — «capetele»
 *  —, iar cu celălalt capăt `q` al grinzii dat, și locul din dreptul lui pe
 *  fiecare piesă prin care trece linia lui: pe lung, în mijlocul piesei când
 *  piesa stă de-a curmezișul grinzii (grinda intră în zid) sau în vârful
 *  dinspre `q` când stă în lungul ei (grinda continuă lamela — acolo o prinde
 *  Pasul 4). Cu axele arhitectului `axe` date, și locul unde fiecare taie
 *  liniile mediane ale piesei. Fără dubluri (două piese se pot atinge în
 *  același punct); cele de pe linie primele, apoi cele de pe ax, ca la
 *  egalitate de distanță să câștige ele. */
export function puncteDeAncorare(e: Esv, nivel: number, q: Pt | null,
                                 axe: readonly AxaArh[] = []): PunctAncorare[] {
  const linie: PunctAncorare[] = [];
  const peAx: PunctAncorare[] = [];
  const rest: PunctAncorare[] = [{ p: [e.nod[0], e.nod[1]], fel: "nod" }];
  for (const pts of amprentaEsv(e, nivel)) {
    const cut = cutiaPiesei(pts);
    const [[x0, x1], [y0, y1]] = cut;
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    rest.push({ p: [cx, cy], fel: "centru" },
              { p: [x0, cy], fel: "capat" }, { p: [x1, cy], fel: "capat" },
              { p: [cx, y0], fel: "capat" }, { p: [cx, y1], fel: "capat" });
    for (const ax of axe) {
      for (const [m0, m1] of [[[x0, cy], [x1, cy]], [[cx, y0], [cx, y1]]] as [Pt, Pt][]) {
        const x = taieAxa(ax, m0, m1);
        if (x) peAx.push({ p: x, fel: "ax" });
      }
    }
    if (!q) continue;
    for (const lg of [0, 1] as const) {
      // linia lui q de-a lungul axei `lg` trece prin piesă?
      const tr: 0 | 1 = lg === 0 ? 1 : 0;
      if (q[tr] < cut[tr][0] - 1e-6 || q[tr] > cut[tr][1] + 1e-6) continue;
      const [l0, l1] = cut[lg];
      const deCurmezis = l1 - l0 <= cut[tr][1] - cut[tr][0];
      const lung = deCurmezis ? (l0 + l1) / 2
        : Math.abs(l0 - q[lg]) <= Math.abs(l1 - q[lg]) ? l0 : l1;
      linie.push({ p: lg === 0 ? [lung, q[tr]] : [q[tr], lung], fel: "linie" });
    }
  }
  const out: PunctAncorare[] = [];
  for (const c of [...linie, ...peAx, ...rest]) {
    if (!out.some((o) => Math.abs(o.p[0] - c.p[0]) <= 1 && Math.abs(o.p[1] - c.p[1]) <= 1))
      out.push(c);
  }
  return out;
}

/** Un punct de ancorare prinde capătul lăsat de om de la cel mult atât (mm),
 *  oricât de larg ar fi cercul cu care editorul caută elementul de sub
 *  cursor: pe o lamelă de 30 × 120 punctele ei stau la 15–60 cm unul de
 *  altul, iar cu 35 cm de lipire (14 px la zoomul obișnuit) ancorarea liberă
 *  dintre ele nu se mai putea atinge — orice clic sărea pe un punct. */
export const TOL_ANCORARE_MM = 150;

/** Unde se prinde capătul grinzii lăsat de om în `p`, pe ESV-ul `e`: punctul
 *  de ancorare ales (`snap`) și locul lui — cel de pe linia celuilalt capăt
 *  oricând e la îndemână (de acolo grinda iese dreaptă), altfel cel mai
 *  apropiat —, sau, fără niciun punct la `tol_mm` (cel mult
 *  `TOL_ANCORARE_MM`), chiar `p`, adus în betonul celei mai apropiate piese,
 *  pe linia celuilalt capăt când ea trece pe acolo, și rotunjit la cm: omul
 *  poate ancora oriunde, lipirea doar îl ajută. */
export type Ancorare = { esv: Esv; p: Pt; snap: PunctAncorare | null; puncte: PunctAncorare[] };
export function ancorareaPeEsv(e: Esv, nivel: number, p: Pt, tol_mm: number,
                               q: Pt | null, axe: readonly AxaArh[] = []): Ancorare | null {
  const piese = amprentaEsv(e, nivel);
  if (!piese.length) return null;
  const puncte = puncteDeAncorare(e, nivel, q, axe);
  const raza = Math.min(tol_mm, TOL_ANCORARE_MM);
  let snap: PunctAncorare | null = null;
  let dMin = raza;
  for (const c of puncte) {
    const d = Math.hypot(c.p[0] - p[0], c.p[1] - p[1]);
    if (d > raza) continue;
    if (snap?.fel === "linie" && c.fel !== "linie") continue;
    if (!snap || (c.fel === "linie" && snap.fel !== "linie") || d < dMin) { snap = c; dMin = d; }
  }
  if (snap) return { esv: e, p: [snap.p[0], snap.p[1]], snap, puncte };
  // liber: în betonul piesei de sub punct (sau celei mai apropiate)
  let liber: Pt | null = null;
  let cutie: [[number, number], [number, number]] | null = null;
  let dLiber = Infinity;
  for (const pts of piese) {
    const cut = cutiaPiesei(pts);
    const [[x0, x1], [y0, y1]] = cut;
    const c: Pt = [Math.min(x1, Math.max(x0, p[0])), Math.min(y1, Math.max(y0, p[1]))];
    const d = Math.hypot(c[0] - p[0], c[1] - p[1]);
    if (d < dLiber) { liber = c; cutie = cut; dLiber = d; }
  }
  if (!liber || !cutie) return null;
  // pe linia celuilalt capăt, când ea trece prin piesă și e la îndemână:
  // capătul lăsat pe lung pe o lamelă nu iese oblic cu câțiva milimetri
  if (q) {
    for (const tr of [0, 1] as const) {
      if (q[tr] >= cutie[tr][0] - 1e-6 && q[tr] <= cutie[tr][1] + 1e-6
          && Math.abs(liber[tr] - q[tr]) <= raza) liber[tr] = q[tr];
    }
  }
  return { esv: e, p: [Math.round(liber[0] / 10) * 10, Math.round(liber[1] / 10) * 10],
           snap: null, puncte };
}

/** Ancorarea pe ESV-ul de sub punctul `p` (cel care îl cuprinde, altfel cel
 *  mai apropiat până la `tol_mm`), fără `fara`; `null` în gol. */
export function ancorareaSubPunct(G: Geometrie, p: Pt, nivel: number, tol_mm: number,
                                  q: Pt | null, axe: readonly AxaArh[] = [],
                                  fara: string | null = null): Ancorare | null {
  const sub = esvSubPunct(G, p, nivel, tol_mm, fara);
  return sub ? ancorareaPeEsv(sub.esv, nivel, p, tol_mm, q, axe) : null;
}

// --- un ESV pus pe o grindă --------------------------------------------------

/** Grinda nivelului `nivel` peste care cade punctul `p` (mm): la cel mult
 *  jumătate din lățimea ei (sau `tol_mm`) de axa ei, între capete — nu peste
 *  betonul lor. Cea mai apropiată, dacă sunt mai multe. */
export function grindaSubPunct(G: Geometrie, p: Pt, nivel: number, tol_mm: number): Grinda | null {
  let best: Grinda | null = null;
  let dMin = Infinity;
  for (const g of G.grinzi) {
    const s = g.sectiune_per_nivel[String(nivel)];
    if (!s) continue;
    const a = capatulGrinzii(G, g, "i");
    const b = capatulGrinzii(G, g, "j");
    if (!a || !b) continue;
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 1) continue;
    const u: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
    const t = (p[0] - a[0]) * u[0] + (p[1] - a[1]) * u[1];
    const d = Math.abs((p[0] - a[0]) * u[1] - (p[1] - a[1]) * u[0]);
    if (d > Math.max(s.b_cm * 5, tol_mm) || d >= dMin) continue;
    if (t <= 0 || t >= L) continue;
    // nu peste betonul capetelor: acolo clicul e al ESV-ului lor
    const peCapat = [g.nod_i, g.nod_j].some((id) => {
      const e = G.esv.find((x) => x.id === id);
      return !!e && amprentaEsv(e, nivel).some((pts) => {
        const xs = pts.map((q) => q[0]);
        const ys = pts.map((q) => q[1]);
        return p[0] >= Math.min(...xs) && p[0] <= Math.max(...xs)
          && p[1] >= Math.min(...ys) && p[1] <= Math.max(...ys);
      });
    });
    if (peCapat) continue;
    best = g; dMin = d;
  }
  return best;
}

/** UN ESV PUS PE O GRINDĂ O ÎMPARTE ÎN DOUĂ. Pe nivelurile pe care există și
 *  grinda, și elementul nou (`id`), grinda `gid` pleacă, iar în locul ei vin
 *  două — de la capătul ei i până la element și de la element până la capătul
 *  j —, cu secțiunile și familia ei. Pe nivelurile fără element rămâne cum
 *  era.
 *
 *  Capătul de pe elementul nou pleacă pe linia vechii grinzi, apoi amândouă
 *  își caută ancorarea din nou (`aseazaGrinzile`): o linie care încape în
 *  betonul capetelor și stă în ziduri — uneori alta decât cea veche, iar
 *  atunci cele două jumătăți pot pleca pe axe diferite.
 *
 *  Ce ținea de grinda veche trece la jumătatea ei: un ESV plantat pe ea, la
 *  jumătatea de sub nodul lui; lista grinzilor de transfer, la amândouă.
 *  -> geometria nouă și id-urile celor două grinzi, sau `null` când grinda și
 *  elementul n-au niciun nivel comun. */
export function imparteGrinda(G: Geometrie, gid: string, id: string,
                              ctx: ContextEditor | null):
    { G: Geometrie; grinzi: [string, string] } | null {
  const g = G.grinzi.find((x) => x.id === gid);
  const e = G.esv.find((x) => x.id === id);
  if (!g || !e) return null;
  const pa = capatulGrinzii(G, g, "i");
  const pb = capatulGrinzii(G, g, "j");
  if (!pa || !pb) return null;
  const comune = Object.keys(g.sectiune_per_nivel).filter((k) => e.sectiune_per_nivel[k]);
  if (!comune.length) return null;
  const rest = Object.keys(g.sectiune_per_nivel).filter((k) => !e.sectiune_per_nivel[k]);
  const sec = (ks: string[]) => Object.fromEntries(ks.map((k) => [k, g.sectiune_per_nivel[k]]));
  const lg: 0 | 1 = Math.abs(pb[0] - pa[0]) >= Math.abs(pb[1] - pa[1]) ? 0 : 1;
  const tr: 0 | 1 = lg === 0 ? 1 : 0;
  // linia vechii grinzi, în dreptul elementului nou
  const t = (e.nod[lg] - pa[lg]) / (pb[lg] - pa[lg] || 1);
  const c = pa[tr] + (pb[tr] - pa[tr]) * t;
  const ofsE: Pt | null = Math.abs(c - e.nod[tr]) < 1e-6 ? null
    : (lg === 0 ? [0, c - e.nod[1]] : [c - e.nod[0], 0]);
  const id1 = idNou(G, "GR");
  const id2 = `GR${Number(id1.slice(2)) + 1}`;
  const g1: Grinda = { ...g, id: id1, nod_j: id, ofs_j: ofsE, sectiune_per_nivel: sec(comune) };
  const g2: Grinda = { ...g, id: id2, nod_i: id, ofs_i: ofsE, sectiune_per_nivel: sec(comune) };
  const ramas: Grinda[] = rest.length ? [{ ...g, sectiune_per_nivel: sec(rest) }] : [];
  const esv = G.esv.map((x) => {
    if (typeof x.baza !== "object" || x.baza.grinda !== gid) return x;
    if (!comune.includes(String(x.baza.nivel))) return x;
    const peI = (x.nod[lg] - pa[lg]) * (e.nod[lg] - x.nod[lg]) >= 0;
    return { ...x, baza: { ...x.baza, grinda: peI ? id1 : id2 } };
  });
  const transfer = G.grinzi_transfer?.flatMap((x) => (x === gid
    ? [...(ramas.length ? [gid] : []), id1, id2] : [x]));
  const G2: Geometrie = {
    ...G, esv,
    grinzi: G.grinzi.flatMap((x) => (x.id === gid ? [...ramas, g1, g2] : [x])),
    ...(transfer ? { grinzi_transfer: transfer } : {}),
  };
  return { G: aseazaGrinzile(G2, [id], ctx, G2), grinzi: [id1, id2] };
}

// --- ieșirile din ziduri ------------------------------------------------------

/** Toleranța desenului (`model.TOLERANTA_DESEN_MM`): cu atât se strâng golurile
 *  înainte de tăiere, iar fâșia mai îngustă de atât de pe fața zidului nu e
 *  ieșire — verdictul solverului lasă afară exact fâșiile astea
 *  (`model.aria_peste_toleranta`). */
export const TOL_DESEN_MM = 25;
/** Cât poate trece un ESV peste fața zidului fără să fie semnalat: aceeași
 *  toleranță a desenului ca a verdictului, nu o hotărâre. */
export const TOL_IESIRE_MM = TOL_DESEN_MM;
/** Până la cât se îngroașă orice zid al arhitecturii ca să țină un element
 *  (`model.PERETE_INGROSAT_MM`): zidul de grosime t crește cu `300 − t` pe
 *  FIECARE parte (`model.pereti_ingrosabili`) — orice element de 30 așezat în
 *  banda asta cuprinde zidul întreg, deci îngroșarea lui locală îl acoperă. */
export const PERETE_INGROSAT_MM = 300;
/** Cât poate ieși betonul din fața zidului unui nivel când zidurile liniei
 *  sunt DECALATE între niveluri (`model.DECALAJ_INTRE_NIVELURI_MM`): o fâșie
 *  de-a lungul elementului, nu mai lată de atât, la un nivel la care elementul
 *  stă întreg în zid la alt nivel suprateran al lui. */
export const DECALAJ_INTRE_NIVELURI_MM = 70;

/** Cât iese un ESV din zidurile arhitecturii la `nivel`: partea amprentei care
 *  nu cade în niciun perete — sau cade într-un gol (ușă, fereastră) —, cu
 *  ADÂNCIMEA ei (latura mică a bucății, în mm: cât trece peste fața zidului)
 *  și bucățile însele, de hașurat pe desen.
 *
 *  Totul în cadrul clădirii, unde zidurile și elementele stau pe X și Y — de
 *  aceea adâncimea e latura mică a cutiei fiecărei bucăți. Calculul se face
 *  față de nodul elementului: coordonatele Stereo 70 au nouă cifre, iar
 *  tăierea poligoanelor își pierde precizia la atâtea. `null` = stă în zid. */
const ariaInelului = (r: readonly (readonly number[])[]): number => {
  let a = 0;
  for (let i = 0; i < r.length - 1; i++) a += r[i][0] * r[i + 1][1] - r[i + 1][0] * r[i][1];
  return Math.abs(a) / 2;
};
const ariaMulti = (m: readonly (readonly (readonly (readonly number[])[])[])[]): number =>
  m.reduce((s, p) => s + p.reduce((t, r, i) => t + (i ? -1 : 1) * ariaInelului(r), 0), 0);
export type Iesire = {
  adancime_mm: number;
  bucati: Pt[][];
  /** nicio bucată din element nu cade în zid: stă de sine stătător */
  afara: boolean;
  /** «solver»: verdictul Pasului 4 venit de pe server (`VerdictAmplasare`),
   *  cu `detaliu` și `tip`; lipsă = regula client, previzualizarea */
  sursa?: "solver";
  tip?: VerdictAmplasare["tip"];
  detaliu?: string;
  arie_cm2?: number | null;
  /** golul (ușă, fereastră) în care stă betonul, când asta e ieșirea cea mai
   *  adâncă: elementul trece prin gol, nu iese din zid */
  gol?: { id: string | null; tip: string | null };
  /** lățimea de-a curmezișul elementului a celei mai late bucăți din afara
   *  zidurilor (fără goluri), în mm — fâșia decalajului dintre niveluri */
  fasie_mm: number;
};
/** Nivelul subteran nu se judecă (verdictul solverului, `operatori._resturile_din_ziduri`):
 *  elementul parterului coboară prin parcare oricum ar fi zidul de acolo. */
export const subteran = (n: NivelContext): boolean => n.level != null && n.level < 0;
export function iesireaDinZiduri(e: Esv, nivel: number, n: NivelContext): Iesire | null {
  const tc = taietor;
  const amp = amprentaEsv(e, nivel);
  if (!tc || !amp.length || subteran(n)) return null;
  const [ox, oy] = e.nod;
  const loc = (pts: Pt[]): [number, number][] => pts.map(([x, y]) => [x - ox, y - oy]);
  const inchis = (r: [number, number][]) => [...r, r[0]];
  const cutie = (pts: readonly (readonly number[])[]) => {
    const xs = pts.map((q) => q[0]);
    const ys = pts.map((q) => q[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  };
  const [a0, b0, a1, b1] = cutie(amp.flat());
  const aproape = (pts: Pt[]) => {
    const [c0, d0, c1, d1] = cutie(pts);
    return c1 >= a0 - 1 && c0 <= a1 + 1 && d1 >= b0 - 1 && d0 <= b1 + 1;
  };
  // Pereții se LĂRGESC cu toleranța înainte de tăiere: fețele unui ESV așezat în zid cad pe fețele zidului la zecimi de
  // milimetru, iar tăierea lasă acolo inele de arie zero — un element cuprins
  // de doi pereți ieșea «30 cm din zid». Un perete aproape pe X/Y devine
  // cutia lui; unul oblic rămâne cum e.
  const rect = (pts: Pt[], d: number): [number, number][] | null => {
    const q = loc(pts);
    const [c0, d0, c1, d1] = cutie(q);
    const aria = ariaInelului(inchis(q));
    if (Math.abs(aria - (c1 - c0) * (d1 - d0)) > 0.02 * aria + 1) return null;
    if (c1 - c0 + 2 * d <= 0 || d1 - d0 + 2 * d <= 0) return [];
    return [[c0 - d, d0 - d], [c1 + d, d0 - d], [c1 + d, d1 + d], [c0 - d, d1 + d]];
  };
  const poli = (lista: { colturi: Pt[] }[], d: (p: { colturi: Pt[] }) => number) => lista
    .filter((p) => p.colturi.length >= 3 && aproape(p.colturi))
    .map((p) => rect(p.colturi, d(p)) ?? loc(p.colturi))
    .filter((r) => r.length >= 3)
    .map((r) => [inchis(r)]);
  const amprenta = amp.map((pts) => [inchis(loc(pts))]);
  const tot = tc.union(amprenta[0], ...amprenta.slice(1));
  // ACEEAȘI regulă ca a verdictului solverului (`model.pereti_ingrosabili`,
  // `operatori._benzile_zidurilor`): orice zid al arhitecturii ține un
  // element de 30 cm — zidul mai subțire se îngroașă cu `300 − t` pe fiecare
  // parte înainte de tăiere, ca elementul de 30 să-l cuprindă oriunde ar sta
  // în banda asta (argumentul plintei: zidul se îngroașă local în jurul
  // lui). Îngroșat doar la 30 în jurul axei, ESV-ul de 30 cu o față pe fața
  // compartimentării de 15 ieșea «7,5 cm din zid» — ceea ce solverul nu
  // socotește ieșire.
  const grosimea = (p: PereteContext): number => {
    if (p.grosime_m != null && p.grosime_m > 0) return p.grosime_m * 1000;
    const [c0, d0, c1, d1] = cutie(loc(p.colturi));
    return Math.min(c1 - c0, d1 - d0);
  };
  const ziduri = poli(n.pereti, (p) =>
    TOL_IESIRE_MM + Math.max(0, PERETE_INGROSAT_MM - grosimea(p as PereteContext)));
  const inAfara = ziduri.length ? tc.difference(tot, ...ziduri) : tot;
  // cea mai mică latură a elementului: mai adânc de atât nu se poate ieși
  const [f0, g0, f1, g1] = cutie(tot.flat(2));
  const plafon = Math.min(f1 - f0, g1 - g0);
  const deACurmezisul = e.axa === "X"
    ? (c: number[]) => c[3] - c[1] : (c: number[]) => c[2] - c[0];
  let adancime = 0;
  let fasie = 0;
  let gol: Iesire["gol"];
  const bucati: Pt[][] = [];
  const ia = (m: typeof tot, inapoi: number, al?: Iesire["gol"]) => {
    for (const poligon of m) {
      const inel = poligon[0];
      if (!inel || inel.length < 4) continue;
      const c = cutie(inel);
      const [c0, d0, c1, d1] = c;
      // o bucată adevărată are arie; inelele degenerate ale tăierii, nu
      if (ariaInelului(inel) < 1 || Math.min(c1 - c0, d1 - d0) < 1) continue;
      const adanc = Math.min(plafon, Math.min(c1 - c0, d1 - d0) + inapoi);
      if (adanc > adancime) { adancime = adanc; gol = al; }
      if (!al) fasie = Math.max(fasie, deACurmezisul(c) + inapoi);
      bucati.push(inel.slice(0, -1).map(([x, y]) => [x + ox, y + oy] as Pt));
    }
  };
  // față de fața ADEVĂRATĂ a zidului: înapoi toleranța cu care s-a lărgit
  ia(inAfara, TOL_IESIRE_MM);
  // golurile strânse cu toleranța desenului, ca la solver
  // (`geometrie._golurile_nivelului`): golul care doar atinge fața zidului
  // nu taie din el; fiecare gol pe rând, ca bucata să-și știe golul
  for (const g of n.goluri) {
    if (!aproape(g.colturi)) continue;
    const [pg] = poli([g], () => -TOL_DESEN_MM);
    if (!pg) continue;
    ia(tc.intersection(tot, pg), 0, { id: g.id, tip: g.tip ?? null });
  }
  if (!bucati.length) return null;
  return { adancime_mm: adancime, bucati, fasie_mm: fasie,
           afara: ariaMulti(inAfara) >= 0.98 * ariaMulti(tot), ...(gol ? { gol } : {}) };
}

/** Ieșirea ESV-ului la `nivel` e DECALAJUL zidului între niveluri, nu o
 *  ieșire (`model.pereti_decalati_intre_niveluri`, `operatori._stau_in_ziduri`):
 *  ce iese e doar o fâșie de-a lungul elementului, nu mai lată de
 *  `DECALAJ_INTRE_NIVELURI_MM` de-a curmezișul (niciun capăt dincolo de zid,
 *  nimic prin gol), iar la alt nivel suprateran al lui elementul stă întreg
 *  în zid. `toate` = nivelurile contextului, pe index. */
export function decalajIntreNiveluri(e: Esv, nivel: number, i: Iesire,
                                     toate: readonly NivelContext[]): boolean {
  if (i.afara || i.gol || i.fasie_mm > DECALAJ_INTRE_NIVELURI_MM) return false;
  return Object.keys(e.sectiune_per_nivel).some((k) => {
    const m = Number(k);
    const n = toate[m];
    return m !== nivel && !!n && !subteran(n) && iesireaDinZiduri(e, m, n) === null;
  });
}

/** Ce refuză verdictul de amplasare al solverului pe un element la un nivel
 *  (`editare.verdictele_amplasarii`): betonul în cameră (`stalp_in_camera`)
 *  sau prin gol (`esv_in_gol`), cu bucățile de desenat, în cadrul clădirii.
 *  `nivel` e indexul nivelului din context. */
export type VerdictAmplasare = {
  element: string;
  nivel: number;
  tip: "stalp_in_camera" | "esv_in_gol";
  detaliu: string;
  arie_cm2?: number | null;
  bucati: Pt[][];
  gol?: string | null;
  tip_gol?: string | null;
  nod_in_gol?: boolean | null;
};

/** Verdictele solverului de la `nivel`, ca ieșiri de desenat, pe id: roșul
 *  editorului e ce refuză Pasul 4, nu o regulă a lui. Un element refuzat și
 *  în cameră, și prin gol are bucățile amândurora; golul rămâne numit. */
export function iesirileDinVerdicte(verdicte: readonly VerdictAmplasare[],
                                    nivel: number): Map<string, Iesire> {
  const out = new Map<string, Iesire>();
  for (const v of verdicte) {
    if (v.nivel !== nivel) continue;
    const gol = v.tip === "esv_in_gol" ? { id: v.gol ?? null, tip: v.tip_gol ?? null } : undefined;
    const gata = out.get(v.element);
    if (gata) {
      gata.bucati = [...gata.bucati, ...v.bucati];
      if (gol && !gata.gol) { gata.gol = gol; gata.detaliu = v.detaliu; gata.tip = v.tip; }
      continue;
    }
    out.set(v.element, { adancime_mm: 0, bucati: [...v.bucati], afara: false, fasie_mm: 0,
                         sursa: "solver", tip: v.tip, detaliu: v.detaliu, arie_cm2: v.arie_cm2,
                         ...(gol ? { gol } : {}) });
  }
  return out;
}

/** ESV-urile care diferă între două geometrii ale aceluiași corp (schimbate,
 *  adăugate, șterse), pe id: cele pe care verdictul de pe server nu le mai
 *  descrie până la verificarea următoare. */
export function esvSchimbate(vechi: Geometrie, nou: Geometrie): string[] {
  const a = new Map(vechi.esv.map((e) => [e.id, JSON.stringify(e)]));
  const b = new Map(nou.esv.map((e) => [e.id, JSON.stringify(e)]));
  const out: string[] = [];
  for (const [id, j] of b) if (a.get(id) !== j) out.push(id);
  for (const id of a.keys()) if (!b.has(id)) out.push(id);
  return out;
}

/** Ieșirile de desenat la `nivel`: verdictele solverului (`verdicte`), iar
 *  pentru elementele schimbate de la ultima verificare (`atinse`) regula
 *  client (`iesireaDinZiduri`), ca previzualizare până răspunde serverul.
 *  Fără verdicte (serverul nu le-a putut da) rămâne regula client pe tot. */
export function iesirileDeDesenat(G: Geometrie, nivel: number, n: NivelContext | null,
                                  toate: readonly NivelContext[],
                                  verdicte: readonly VerdictAmplasare[] | null | undefined,
                                  atinse: ReadonlySet<string>): Map<string, Iesire> {
  if (!verdicte) return iesirileNivelului(G, nivel, n, toate);
  const out = iesirileDinVerdicte(verdicte, nivel);
  for (const id of atinse) {
    out.delete(id);
    const e = G.esv.find((x) => x.id === id);
    if (!e || !n || !sectiunea(e, nivel)) continue;
    const i = iesireaDinZiduri(e, nivel, n);
    if (i && !decalajIntreNiveluri(e, nivel, i, toate)) out.set(id, i);
  }
  return out;
}

/** Ieșirile din ziduri ale tuturor ESV-urilor de la `nivel`, pe id. Cu
 *  `toate` (nivelurile contextului) decalajul zidului între niveluri e iertat
 *  (`decalajIntreNiveluri`). */
export function iesirileNivelului(G: Geometrie, nivel: number,
                                  n: NivelContext | null,
                                  toate?: readonly NivelContext[]): Map<string, Iesire> {
  const out = new Map<string, Iesire>();
  if (!n) return out;
  for (const e of G.esv) {
    if (!sectiunea(e, nivel)) continue;
    const i = iesireaDinZiduri(e, nivel, n);
    if (i && !(toate && decalajIntreNiveluri(e, nivel, i, toate))) out.set(e.id, i);
  }
  return out;
}

// --- cotele de lucru ---------------------------------------------------------

/** O cotă a ESV-ului ales, pe ecran. `fel` spune ce face o valoare scrisă:
 *  «L»/«b» redimensionează, «dist» mută elementul pe `axa` în sensul `semn`
 *  (spre obstacolul măsurat) până lasă golul cerut. */
export type CotaEsv = {
  a: Pt;
  b: Pt;
  valoare: number;
  fel: "L" | "b" | "dist";
  axa: 0 | 1;
  semn: 1 | -1;
};

/** Cât de departe se caută un obstacol pentru cotele de distanță. */
const RAZA_COTA_M = 12;
/** Cât se poate abate lateral un element ca să fie «următorul» pe o direcție
 *  fără să stea chiar în fața celui ales: cota măsoară atunci golul pe axa
 *  direcției, ca pe orice planșă. */
const RAZA_LATERALA_M = 3;

/** COTELE ESV-ULUI ALES, cele pe care omul le scrie direct pe desen.
 *
 *  Două de mărime — latura lungă și grosimea, pe fețele elementului — și cele
 *  de POZIȚIE: pe fiecare din cele patru direcții, golul de la fața
 *  elementului până la URMĂTORUL ELEMENT STRUCTURAL — un alt ESV al nivelului
 *  —, iar într-un zid, de-a lungul lui, și până la capătul zidului, dacă e mai
 *  aproape. Întâi elementele care stau chiar în fața lui; fără ele, cel mai
 *  apropiat dintre cele abătute lateral cel mult `RAZA_LATERALA_M`. */
export function coteleEsv(c: Cadru, G: Geometrie, e: Esv, nivel: number,
                          pereti: AxaPerete[], departare: number): CotaEsv[] {
  const cut = cutiaEsv(c, e, nivel);
  if (!cut) return [];
  const out: CotaEsv[] = [];
  const [x0, x1] = cut.x;
  const [y0, y1] = cut.y;
  // mărimile: latura pe X deasupra, cea pe Y în stânga
  const peX = e.axa === "X" ? "L" : "b";
  const peY = e.axa === "X" ? "b" : "L";
  out.push({ a: [x0, y0 - departare], b: [x1, y0 - departare], valoare: x1 - x0,
             fel: peX, axa: 0, semn: 1 });
  out.push({ a: [x0 - departare, y0], b: [x0 - departare, y1], valoare: y1 - y0,
             fel: peY, axa: 1, semn: 1 });

  const nod = laEcran(c, e.nod);
  const gazda = pereti.find((a) => {
    const [lung, trans] = a.axa === "X" ? [nod[0], nod[1]] : [nod[1], nod[0]];
    return lung >= a.de - 1e-3 && lung <= a.la + 1e-3
      && Math.abs(trans - a.cota) <= a.grosime / 2 + 1e-3;
  });
  const alte = G.esv.filter((x) => x.id !== e.id)
    .map((x) => cutiaEsv(c, x, nivel)).filter((x): x is Cutie => !!x);
  const directii: { axa: 0 | 1; semn: 1 | -1 }[] =
    [{ axa: 0, semn: -1 }, { axa: 0, semn: 1 }, { axa: 1, semn: -1 }, { axa: 1, semn: 1 }];
  for (const d of directii) {
    const fata = d.axa === 0 ? (d.semn > 0 ? x1 : x0) : (d.semn > 0 ? y1 : y0);
    const [s0, s1] = d.axa === 0 ? [y0, y1] : [x0, x1];
    // [abaterea laterală > 0, golul]: cine stă în față bate pe cine e alături
    let best: [number, number] = [1, RAZA_COTA_M];
    const ia = (rang: number, dist: number) => {
      if (dist < -1e-6 || dist >= RAZA_COTA_M) return;
      if (rang < best[0] || (rang === best[0] && dist < best[1])) best = [rang, Math.max(0, dist)];
    };
    for (const o of alte) {
      const [o0, o1] = d.axa === 0 ? o.y : o.x;
      const lat = Math.max(0, o0 - s1, s0 - o1);
      if (lat > RAZA_LATERALA_M) continue;
      const fo = d.axa === 0 ? (d.semn > 0 ? o.x[0] : o.x[1]) : (d.semn > 0 ? o.y[0] : o.y[1]);
      ia(lat > 1e-6 ? 1 : 0, (fo - fata) * d.semn);
    }
    if (gazda && gazda.axa === (d.axa === 0 ? "X" : "Y")) {
      const capat = d.semn > 0 ? gazda.la : gazda.de;
      ia(0, (capat - fata) * d.semn);
    }
    const dist = best[1];
    if (dist >= RAZA_COTA_M || dist < 0.005) continue;
    // sub element (în dreapta lui, pe Y): pe axa lui, cifra ar sta pe beton
    const pe = d.axa === 0 ? y1 + departare : x1 + departare;
    const a: Pt = d.axa === 0 ? [fata, pe] : [pe, fata];
    const b: Pt = d.axa === 0 ? [fata + d.semn * dist, pe] : [pe, fata + d.semn * dist];
    out.push({ a, b, valoare: dist, fel: "dist", axa: d.axa, semn: d.semn });
  }
  return out;
}

/** O valoare scrisă pe o cotă de distanță, ca deplasare a nodului pe ecran:
 *  obiectul pleacă exact cât să lase golul cerut. */
export function deplasareaCotei(c: CotaEsv, scris: number): Pt {
  const d = -c.semn * (scris - c.valoare);
  return c.axa === 0 ? [d, 0] : [0, d];
}

// --- cotele tronsonului -------------------------------------------------------

/** O cotă de citit (nu de scris), pe ecran. */
export type CotaSimpla = { a: Pt; b: Pt; valoare: number };

/** Conturul tronsonului la nivelul ăsta, pe ecran: amprenta nivelului, fără
 *  punctele dublate și fără cele de pe mijlocul unei laturi drepte; fără
 *  amprentă, cutia zidurilor. `null` când nivelul n-are nici una, nici alta. */
export function conturulTronsonului(c: Cadru, n: NivelContext): Pt[] | null {
  let pts: Pt[] = (n.amprenta ?? []).map((q) => laEcran(c, q));
  if (pts.length < 3) {
    const toate = n.pereti.flatMap((p) => p.colturi.map((q) => laEcran(c, q)));
    if (!toate.length) return null;
    const xs = toate.map((q) => q[0]);
    const ys = toate.map((q) => q[1]);
    const [a0, a1, b0, b1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    pts = [[a0, b0], [a1, b0], [a1, b1], [a0, b1]];
  }
  const TOL = 0.005;
  // punctele dublate (și închiderea repetată a inelului)
  let q = pts.filter((p, i) => {
    const r = pts[(i + 1) % pts.length];
    return Math.hypot(r[0] - p[0], r[1] - p[1]) > TOL;
  });
  // punctele de pe o latură dreaptă: până nu mai pleacă niciunul
  for (let schimbat = true; schimbat && q.length > 3;) {
    schimbat = false;
    for (let i = 0; i < q.length; i++) {
      const a = q[(i + q.length - 1) % q.length], p = q[i], b = q[(i + 1) % q.length];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const abatere = L > 0 ? Math.abs((p[0] - a[0]) * (b[1] - a[1]) - (p[1] - a[1]) * (b[0] - a[0])) / L : 0;
      if (abatere <= TOL) { q = q.filter((_, j) => j !== i); schimbat = true; break; }
    }
  }
  return q.length >= 3 ? q : null;
}

const inPoligon = (p: Pt, poli: Pt[]): boolean => {
  let in_ = false;
  for (let i = 0, j = poli.length - 1; i < poli.length; j = i++) {
    const [xi, yi] = poli[i], [xj, yj] = poli[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) in_ = !in_;
  }
  return in_;
};

/** COTELE LATURILOR TRONSONULUI, în afara conturului, la `departare`: un
 *  dreptunghi le are pe cele două care îl spun întreg — lățimea deasupra,
 *  adâncimea în stânga —, orice altă formă câte una pe fiecare latură (un L
 *  are șase). */
export function coteleTronsonului(contur: Pt[], departare: number): CotaSimpla[] {
  const n = contur.length;
  const laturi = contur.map((a, i) => [a, contur[(i + 1) % n]] as [Pt, Pt]);
  const pePlan = (a: Pt, b: Pt) => Math.abs(a[0] - b[0]) < 1e-3 || Math.abs(a[1] - b[1]) < 1e-3;
  const dreptunghi = n === 4 && laturi.every(([a, b]) => pePlan(a, b));
  const out: CotaSimpla[] = [];
  const cota = ([a, b]: [Pt, Pt]) => {
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 0.01) return;
    // normala spre afară: partea în care mijlocul laturii, împins puțin, nu
    // mai cade în contur
    let nx = (b[1] - a[1]) / L, ny = -(b[0] - a[0]) / L;
    const m: Pt = [(a[0] + b[0]) / 2 + nx * 1e-3, (a[1] + b[1]) / 2 + ny * 1e-3];
    if (inPoligon(m, contur)) { nx = -nx; ny = -ny; }
    out.push({ a: [a[0] + nx * departare, a[1] + ny * departare],
               b: [b[0] + nx * departare, b[1] + ny * departare], valoare: L });
  };
  if (dreptunghi) {
    const xs = contur.map((p) => p[0]);
    const ys = contur.map((p) => p[1]);
    const sus = laturi.find(([a, b]) => Math.abs(a[1] - b[1]) < 1e-3
      && Math.abs(a[1] - Math.min(...ys)) < 1e-3);
    const stanga = laturi.find(([a, b]) => Math.abs(a[0] - b[0]) < 1e-3
      && Math.abs(a[0] - Math.min(...xs)) < 1e-3);
    if (sus) cota(sus);
    if (stanga) cota(stanga);
    return out;
  }
  laturi.forEach(cota);
  return out;
}
